/**
 * Company Financial Info Mock Data Panel
 *
 * 开发环境下的快捷填充工具
 * 提供可视化的面板用于快速填充公司财务状况数据
 *
 * @module mockDataGenerator.demo
 */

import { Button } from "antd";
import { FormInstance } from "antd/lib/form";
import { useState } from "react";
import {
  companyFinancialPresets,
  generateCompanyFinancialMockData,
} from "./mockDataGenerator.company-financial";

interface CompanyFinancialMockDataPanelProps {
  /** 表单实例 */
  form?: FormInstance;
}

/**
 * 公司财务状况 Mock 数据生成工具面板
 */
export function CompanyFinancialMockDataPanel({
  form,
}: CompanyFinancialMockDataPanelProps) {
  const [visible, setVisible] = useState(false);

  if (!form) {
    return null;
  }

  /* ============================================================
   * 预设场景操作
   * ========================================================== */

  const handleFillSmallProfitable = () => {
    const data = companyFinancialPresets.smallProfitable();
    form.setFieldsValue(data);
    console.log("✅ 已填充小型盈利企业数据:", data);
  };

  const handleFillMediumProfitable = () => {
    const data = companyFinancialPresets.mediumProfitable();
    form.setFieldsValue(data);
    console.log("✅ 已填充中型盈利企业数据:", data);
  };

  const handleFillLargeProfitable = () => {
    const data = companyFinancialPresets.largeProfitable();
    form.setFieldsValue(data);
    console.log("✅ 已填充大型盈利企业数据:", data);
  };

  const handleFillLossmaking = () => {
    const data = companyFinancialPresets.lossmaking();
    form.setFieldsValue(data);
    console.log("✅ 已填充亏损企业数据:", data);
  };

  const handleFillHighVaRatio = () => {
    const data = companyFinancialPresets.highVaRatio();
    form.setFieldsValue(data);
    console.log("✅ 已填充高虚拟资产占比企业数据:", data);
  };

  const handleFillLowVaRatio = () => {
    const data = companyFinancialPresets.lowVaRatio();
    form.setFieldsValue(data);
    console.log("✅ 已填充低虚拟资产占比企业数据:", data);
  };

  const handleFillWithOtherSources = () => {
    const data = companyFinancialPresets.withOtherSources();
    form.setFieldsValue(data);
    console.log("✅ 已填充包含其他来源的数据:", data);
  };

  const handleFillRandom = () => {
    const data = generateCompanyFinancialMockData();
    form.setFieldsValue(data);
    console.log("✅ 已填充随机财务数据:", data);
  };

  const handleClear = () => {
    form.resetFields();
    console.log("✅ 已清空财务信息表单");
  };

  const handleToggle = () => {
    setVisible(!visible);
  };

  return (
    <>
      {/* 浮动按钮 */}
      {!visible && (
        <div
          className="fixed bottom-8 right-8 z-[9999] group cursor-pointer"
          onClick={handleToggle}
          style={{
            width: "64px",
            height: "64px",
          }}
        >
          <div
            className="absolute inset-0 rounded-full transition-all duration-300 group-hover:scale-110"
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              boxShadow:
                "0 10px 25px -5px rgba(102, 126, 234, 0.5), 0 8px 10px -6px rgba(102, 126, 234, 0.3)",
            }}
          />
          <div
            className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
            style={{
              background: "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
              boxShadow: "0 15px 35px -5px rgba(118, 75, 162, 0.6)",
            }}
          />
          <div className="relative w-full h-full flex items-center justify-center text-3xl transition-transform duration-300 group-hover:scale-110 group-active:scale-95">
            💰
          </div>
          <div
            className="absolute inset-0 rounded-full animate-ping opacity-20"
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              animationDuration: "3s",
            }}
          />
        </div>
      )}

      {/* 主面板 */}
      {visible && (
        <div
          className="fixed bottom-8 right-8 z-[9999] bg-white rounded-lg shadow-2xl"
          style={{
            width: "320px",
            maxHeight: "600px",
            display: "flex",
            flexDirection: "column",
            border: "1px solid #d9d9d9",
          }}
        >
          {/* 标题栏 */}
          <div
            className="flex items-center justify-between px-4 py-3"
            style={{
              borderBottom: "1px solid #f0f0f0",
              backgroundColor: "#fafafa",
              borderRadius: "8px 8px 0 0",
            }}
          >
            <span className="font-semibold text-base">
              💰 财务状况 Mock工具
            </span>
            <Button size="small" onClick={handleToggle}>
              ✕
            </Button>
          </div>

          {/* 内容区域 */}
          <div
            className="overflow-y-auto p-4 space-y-4"
            style={{ maxHeight: "500px" }}
          >
            {/* 预设场景 */}
            <div className="space-y-2">
              <div className="text-sm font-semibold text-gray-700">
                预设场景
              </div>
              <Button size="small" block onClick={handleFillSmallProfitable}>
                小型盈利企业
              </Button>
              <Button size="small" block onClick={handleFillMediumProfitable}>
                中型盈利企业
              </Button>
              <Button size="small" block onClick={handleFillLargeProfitable}>
                大型盈利企业
              </Button>
              <Button size="small" block onClick={handleFillLossmaking}>
                亏损企业
              </Button>
              <Button size="small" block onClick={handleFillHighVaRatio}>
                高虚拟资产占比
              </Button>
              <Button size="small" block onClick={handleFillLowVaRatio}>
                低虚拟资产占比
              </Button>
              <Button size="small" block onClick={handleFillWithOtherSources}>
                包含其他来源
              </Button>
            </div>

            {/* 分割线 */}
            <div className="border-t border-gray-200" />

            {/* 其他操作 */}
            <div className="space-y-2">
              <div className="text-sm font-semibold text-gray-700">
                其他操作
              </div>
              <Button size="small" block onClick={handleFillRandom}>
                随机生成
              </Button>
            </div>
          </div>

          {/* 清空按钮 */}
          <div className="px-4 pb-4 pt-2 border-t">
            <Button danger size="small" block onClick={handleClear}>
              清空表单
            </Button>
          </div>
        </div>
      )}
    </>
  );
}

export default CompanyFinancialMockDataPanel;
